import {
  addDays,
  addMonths,
  format,
  endOfMonth,
  endOfWeek as dateFnsEndOfWeek,
} from "date-fns";
import { Income, ExpenseSection, SectionTotal } from "./types";
import { getTodayLocal, isTodayLocal, isTomorrowLocal } from "./dateUtils";

// Timezone-aware date comparison utilities
const isTodayUTC = isTodayLocal;
const isTomorrowUTC = isTomorrowLocal;

const isSameMonthUTC = (
  date1: Date | null | undefined,
  date2: Date
): boolean => {
  if (!date1 || !(date1 instanceof Date)) return false;
  return (
    date1.getUTCFullYear() === date2.getUTCFullYear() &&
    date1.getUTCMonth() === date2.getUTCMonth()
  );
};

// Calculate the amount owed (invoiceTotal - paidToDate)
export const calculateOwed = (income: Income): number => {
  const invoiceTotal =
    typeof income.invoiceTotal === "number" && !isNaN(income.invoiceTotal)
      ? income.invoiceTotal
      : parseFloat(income.invoiceTotal?.toString() || "0") || 0;
  const paidToDate =
    typeof income.paidToDate === "number" && !isNaN(income.paidToDate)
      ? income.paidToDate
      : parseFloat(income.paidToDate?.toString() || "0") || 0;

  return invoiceTotal - paidToDate;
};

// Get the invoice total (replacing owed amount concept)
export const getInvoiceTotal = (income: Income): number => {
  return typeof income.invoiceTotal === "number" && !isNaN(income.invoiceTotal)
    ? income.invoiceTotal
    : parseFloat(income.invoiceTotal?.toString() || "0") || 0;
};

export const getNetDepositedTotal = (income: Income): number => {
  const invoiceTotal = getInvoiceTotal(income);
  const processorFee =
    typeof income.paymentProcessorFee === "number" &&
    !isNaN(income.paymentProcessorFee)
      ? income.paymentProcessorFee
      : parseFloat(income.paymentProcessorFee?.toString() || "0") || 0;
  const loanRepayment =
    typeof income.loanRepayment === "number" && !isNaN(income.loanRepayment)
      ? income.loanRepayment
      : parseFloat(income.loanRepayment?.toString() || "0") || 0;
  return invoiceTotal - processorFee - loanRepayment;
};

export const isPendingDeposit = (income: Income): boolean => {
  if (!income.receivedDate || !income.depositDate) return false;
  if (!(income.depositDate instanceof Date)) return false;
  const todayUTC = getTodayLocal();
  return income.depositDate.getTime() > todayUTC.getTime();
};

export const categorizeIncome = (
  incomeItems: Income[]
): Record<ExpenseSection, Income[]> => {
  // Create a UTC date representing today at midnight, based on user's timezone
  const todayUTC = getTodayLocal();
  const tomorrowUTC = addDays(todayUTC, 1);
  // Use actual calendar week end (Sunday) instead of adding 7 days
  const endOfWeek = dateFnsEndOfWeek(todayUTC, { weekStartsOn: 0 }); // 0 = Sunday start
  const next10Days = addDays(todayUTC, 10);
  const nextMonth = addMonths(todayUTC, 1);
  const currentMonthEnd = endOfMonth(todayUTC);
  const expected90Days = addDays(todayUTC, 90);

  // Create a deep copy of each income item to prevent double-counting in totals
  const result = {
    received: [] as Income[],
    pendingDeposit: [] as Income[],
    overdue: [] as Income[],
    today: [] as Income[],
    tomorrow: [] as Income[],
    thisWeek: [] as Income[],
    next10Days: [] as Income[],
    thisMonth: [] as Income[],
    nextMonth: [] as Income[],
    expected: [] as Income[],
    upcoming: [] as Income[],
  };

  const addByDate = (acc: typeof result, item: Income, targetDate: Date) => {
    // Time-based categorization (mutually exclusive timing categories)
    if (targetDate < todayUTC && !isTodayUTC(targetDate)) {
      acc.overdue.push({ ...item });
    } else if (isTodayUTC(targetDate)) {
      acc.today.push({ ...item });
    } else if (isTomorrowUTC(targetDate)) {
      acc.tomorrow.push({ ...item });
    } else if (
      targetDate >= addDays(tomorrowUTC, 1) &&
      targetDate <= endOfWeek
    ) {
      acc.thisWeek.push({ ...item });
    }

    // "Within 10 Days" should not duplicate the more specific buckets.
    // If something is in Within 7 Days (thisWeek) it should not also appear here.
    const isInThisWeekRange =
      targetDate >= addDays(tomorrowUTC, 1) && targetDate <= endOfWeek;
    const isInToday = isTodayUTC(targetDate);
    const isInTomorrow = isTomorrowUTC(targetDate);

    if (
      targetDate >= todayUTC &&
      targetDate <= next10Days &&
      !isInToday &&
      !isInTomorrow &&
      !isInThisWeekRange
    ) {
      acc.next10Days.push({ ...item });
    }

    if (
      targetDate > currentMonthEnd &&
      targetDate <= expected90Days &&
      !acc.upcoming.some((existing) => existing.id === item.id)
    ) {
      acc.upcoming.push({ ...item });
    }

    const isCurrentMonth = isSameMonthUTC(targetDate, todayUTC);
    const isNextMonth = isSameMonthUTC(targetDate, nextMonth);
    const isOverdue = targetDate < todayUTC && !isTodayUTC(targetDate);

    // "Rest of the Month" should exclude anything already in the 10-day window.
    // It is still within the current calendar month, just after Within 10 Days.
    if (isCurrentMonth && !isOverdue && targetDate > next10Days) {
      acc.thisMonth.push({ ...item });
    }

    if (isNextMonth) {
      acc.nextMonth.push({ ...item });
    }

    if (
      targetDate > currentMonthEnd &&
      targetDate <= expected90Days &&
      !acc.expected.some((existing) => existing.id === item.id)
    ) {
      acc.expected.push({ ...item });
    }
  };

  return incomeItems.reduce((acc, income) => {
    // Daily-recurring income is an explicit weekday schedule the user wants on
    // the date cards, so it is bucketed even when its type is Prospective
    // (other Prospective income is shown only in the separate Prospective
    // section). Mirrors the split in IncomePage.
    const isDailyRecurring =
      Boolean(income.isRecurring) &&
      (income.recurrencePattern || "").toLowerCase().startsWith("daily");
    if ((income.type || "Actual") === "Prospective" && !isDailyRecurring) {
      return acc;
    }
    const { expectedByDate, isPaid, receivedDate, depositDate } = income;

    if (receivedDate) {
      // Only include "Received" items in the current month.
      // Older received income is surfaced via the Past Months section instead.
      if (isSameMonthUTC(receivedDate, todayUTC)) {
        acc.received.push({ ...income });
      }
      const pendingDeposit = isPendingDeposit(income);
      if (pendingDeposit && depositDate instanceof Date) {
        acc.pendingDeposit.push({
          ...income,
          expectedByDate: depositDate,
        });
        const ghost: Income = {
          ...income,
          id: `${income.id}-deposit-ghost`,
          expectedByDate: depositDate,
          receivedDate: undefined,
          isPaid: false,
          isDepositGhost: true,
        };
        addByDate(acc, ghost, depositDate);
      }
      return acc;
    }

    if (isPaid) return acc;

    if (!expectedByDate || !(expectedByDate instanceof Date)) return acc;

    addByDate(acc, { ...income }, expectedByDate);
    return acc;
  }, result);
};

export const calculateIncomeSectionTotals = (
  incomeBySection: Record<ExpenseSection, Income[]>
): SectionTotal[] => {
  return Object.entries(incomeBySection).map(([section, incomeItems]) => {
    const total = incomeItems.reduce((sum, income) => {
      if (section === "received" && isPendingDeposit(income)) {
        return sum;
      }
      return sum + getNetDepositedTotal(income);
    }, 0);
    return {
      section: section as ExpenseSection,
      total,
    };
  });
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

// Get the current month name (timezone-aware)
export const getCurrentMonthName = (): string => {
  const todayUTC = getTodayLocal();
  return format(todayUTC, "MMMM");
};

// Get the next month name (timezone-aware)
export const getNextMonthName = (): string => {
  const todayUTC = getTodayLocal();
  return format(addMonths(todayUTC, 1), "MMMM");
};

export type PastMonthBucket = {
  key: string; // YYYY-MM
  label: string; // e.g. "January 2026"
  items: Income[];
  total: number;
};

// Past months = prior months only (excludes current month), and only includes
// items that were actually received or explicitly marked paid. Unpaid items
// with expected dates in the past remain in Overdue instead of appearing here.
export const getPastMonthsIncomeBuckets = (
  incomeItems: Income[],
  monthsBack: number = 12,
  referenceDate?: Date
): PastMonthBucket[] => {
  const nowUTC = referenceDate ?? getTodayLocal();
  const startOfCurrentMonthUTC = new Date(
    Date.UTC(nowUTC.getUTCFullYear(), nowUTC.getUTCMonth(), 1)
  );

  const getIncomeDate = (income: Income): Date | null => {
    const date = income.receivedDate ?? income.expectedByDate;
    return date instanceof Date ? date : null;
  };

  const buckets: PastMonthBucket[] = [];
  for (let i = 1; i <= monthsBack; i++) {
    const start = new Date(
      Date.UTC(nowUTC.getUTCFullYear(), nowUTC.getUTCMonth() - i, 1)
    );
    const end = new Date(
      Date.UTC(nowUTC.getUTCFullYear(), nowUTC.getUTCMonth() - i + 1, 1)
    );
    const year = start.getUTCFullYear();
    const monthIndex = start.getUTCMonth(); // 0..11

    const items = incomeItems
      .filter((income) => {
        if ((income.type || "Actual") === "Prospective") return false;
        if (!income.receivedDate && !income.isPaid) return false;
        const date = getIncomeDate(income);
        if (!date) return false;
        if (date >= startOfCurrentMonthUTC) return false;
        return date >= start && date < end;
      })
      .sort((a, b) => {
        const da = getIncomeDate(a)?.getTime() ?? 0;
        const db = getIncomeDate(b)?.getTime() ?? 0;
        return db - da;
      });

    const total = items.reduce((sum, item) => sum + getInvoiceTotal(item), 0);

    buckets.push({
      key: `${year}-${String(monthIndex + 1).padStart(2, "0")}`,
      // Use a "safe" UTC noon timestamp to avoid local timezone month shifts.
      label: format(new Date(Date.UTC(year, monthIndex, 1, 12)), "MMMM yyyy"),
      items,
      total,
    });
  }

  return buckets;
};

// Get all income for the current month (both paid and unpaid)
// Includes items whose expectedByDate OR receivedDate falls in the current month
// Uses UTC month boundaries to avoid timezone leakage
export const getCurrentMonthIncome = (incomeItems: Income[]): Income[] => {
  const nowUTC = getTodayLocal();
  const year = nowUTC.getUTCFullYear();
  const month = nowUTC.getUTCMonth();
  const firstDayOfMonth = new Date(Date.UTC(year, month, 1));
  const firstDayOfNextMonth = new Date(Date.UTC(year, month + 1, 1));

  const isInCurrentMonth = (date: Date | undefined): boolean => {
    if (!date || !(date instanceof Date)) return false;
    return date >= firstDayOfMonth && date < firstDayOfNextMonth;
  };

  return incomeItems.filter((income) => {
    return (
      isInCurrentMonth(income.expectedByDate) ||
      isInCurrentMonth(income.receivedDate)
    );
  });
};

// An income item is suspended when it is flagged and either has no end date
// (indefinite) or the end date is still in the future. Suspended income stays
// visible in breakdown lists but is excluded from headline totals.
export const isIncomeSuspended = (income: Income): boolean => {
  if (!income.isSuspended) return false;
  if (!income.suspendedUntil) return true;
  const until = new Date(income.suspendedUntil);
  if (isNaN(until.getTime())) return true;
  return getTodayLocal() < until;
};

export const getActiveIncome = (incomeItems: Income[]): Income[] =>
  incomeItems.filter((income) => !isIncomeSuspended(income));

// Calculate the total of all income for the current month.
// Counts what is still owed, not the gross invoice value: money already
// collected was income when it was received, so a record that has been paid in
// full contributes nothing. The gross figure stays on the record and is shown
// alongside the balance in the breakdown.
export const getCurrentMonthIncomeTotal = (incomeItems: Income[]): number => {
  const currentMonthIncome = getActiveIncome(
    getCurrentMonthIncome(incomeItems)
  );
  return currentMonthIncome.reduce(
    (total, income) => total + calculateOwed(income),
    0
  );
};

// Calculate the total *expected* income for the current month: the gross
// invoice value of every active income record dated this month, regardless of
// how much has already been collected. This is the "Income › Expected" figure
// on the dashboard — what you invoiced/expect for the month — as opposed to the
// owed balance (getCurrentMonthIncomeTotal) or what has actually landed in the
// bank (received, derived from transactions).
export const getCurrentMonthExpectedIncomeTotal = (
  incomeItems: Income[]
): number => {
  const currentMonthIncome = getActiveIncome(
    getCurrentMonthIncome(incomeItems)
  );
  return currentMonthIncome.reduce(
    (total, income) => total + getInvoiceTotal(income),
    0
  );
};

// Get remaining (unpaid) income for the current month
export const getRemainingMonthIncome = (incomeItems: Income[]): Income[] => {
  const currentMonthIncome = getCurrentMonthIncome(incomeItems);
  return currentMonthIncome.filter((income) => !income.isPaid);
};

// Calculate the total of remaining (unpaid) income for the current month
export const getRemainingMonthIncomeTotal = (incomeItems: Income[]): number => {
  const remainingIncome = getActiveIncome(getRemainingMonthIncome(incomeItems));
  return remainingIncome.reduce(
    (total, income) => total + calculateOwed(income),
    0
  );
};
